import React from "react";
import { useNavigate } from "react-router-dom";

// reusable components
import { Spinner } from "@/components/ui/Spinner";
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import { StatusBadge } from "@/utils/resolveStatusStyle";

// hooks and utils
import useTransactionDetails from "./hooks/useTransactionDetails";
import { TRANSACTION_TYPE, TRANSACTION_STATUS } from "@/utils/statusStyles";
import { formatDateTime } from "@/utils/formatDateTime";
import { formatCurrency } from "@/utils/formatCurrency";

// inhouse icons
import { ArrowLeft } from "@/components/icons_new";
import { useStorePath } from "@hooks/useStorePath";

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-[#f0f0f0] last:border-0">
      <span className="text-sm text-[#777]">{label}</span>
      <span className="text-sm font-medium text-[#1a1a1a] text-right">{value ?? "—"}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-[#e4e4e4] bg-white p-5 shadow-sm">
      {title && <h3 className="mb-3 text-sm font-semibold text-[#333]">{title}</h3>}
      {children}
    </div>
  );
}

export default function TransactionDetails() {
  const navigate = useNavigate();
  const { storePath } = useStorePath();
  const { transaction, isLoading, isError, refetch } = useTransactionDetails();

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 pt-12">
        <Spinner size="medium" />
        <p className="text-sm text-[#4b4b4b]">Loading transaction...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-6 pt-6">
        <EmptyStateCard
          title="Failed to load transaction"
          description="Please try refreshing the page or check your connection."
          buttonLabel="Retry"
          onAction={refetch}
        />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="px-6 pt-6">
        <EmptyStateCard
          title="Transaction not found"
          description="We could not locate this transaction. It may have been removed."
          buttonLabel="Go back"
          onAction={() => navigate(storePath("transactions"))}
        />
      </div>
    );
  }

  const { meta = {}, payments = [] } = transaction;

  return (
    <div className="flex flex-col gap-4 px-6 pt-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate(storePath("transactions"))}
          className="flex items-center gap-2 text-sm font-medium text-[#555] hover:text-black"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to transactions
        </button>
        <div className="flex items-center gap-2">
          <StatusBadge map={TRANSACTION_TYPE} status={transaction.type} />
          <StatusBadge map={TRANSACTION_STATUS} status={transaction.status} />
        </div>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-[#1a1a1a]">
          {transaction.transactionNumber}
        </h1>
        <p className="text-sm text-[#777]">{formatDateTime(transaction.date)}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left: amounts + payments */}
        <div className="space-y-6 lg:col-span-2">
          <Card title="Amount Summary">
            <InfoRow label="Subtotal" value={formatCurrency(transaction.subTotal)} />
            <InfoRow label="Tax" value={formatCurrency(transaction.taxAmount)} />
            <InfoRow label="Items" value={transaction.itemsCount} />
            <div className="flex items-center justify-between gap-4 pt-3">
              <span className="text-base font-semibold text-[#1a1a1a]">Total</span>
              <span
                className={`text-base font-bold ${
                  transaction.total < 0 ? "text-red-600" : "text-[#1a1a1a]"
                }`}
              >
                {formatCurrency(transaction.total)}
              </span>
            </div>
          </Card>

          <Card title="Payments">
            {payments.length === 0 ? (
              <p className="text-sm text-[#999] italic">No payments recorded.</p>
            ) : (
              <div className="divide-y divide-[#f0f0f0]">
                {payments.map((payment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-[#1a1a1a]">{payment.method}</p>
                      {payment.reference && (
                        <p className="text-xs text-[#999]">Ref: {payment.reference}</p>
                      )}
                    </div>
                    <span
                      className={`text-sm font-semibold ${
                        payment.amount < 0 ? "text-red-600" : "text-[#1a1a1a]"
                      }`}
                    >
                      {formatCurrency(payment.amount)}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 pt-3">
                  <span className="text-sm font-semibold text-[#555]">Amount Paid</span>
                  <span className="text-sm font-semibold text-[#1a1a1a]">
                    {formatCurrency(meta.amountPaid)}
                  </span>
                </div>
                {meta.balanceDue > 0 && (
                  <div className="flex items-center justify-between gap-4 pt-2">
                    <span className="text-sm font-semibold text-[#555]">Balance Due</span>
                    <span className="text-sm font-semibold text-red-600">
                      {formatCurrency(meta.balanceDue)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Right: meta */}
        <div className="space-y-6">
          <Card title="Details">
            <InfoRow
              label="Customer"
              value={transaction.customer?.name || "Walk-in"}
            />
            <InfoRow label="Cashier" value={transaction.cashier} />
            <InfoRow label="Location" value={transaction.location} />
            <InfoRow label="Counter" value={transaction.counter} />
          </Card>
        </div>
      </div>
    </div>
  );
}
